import {
  AdminAuditEventPageSchema,
  AdminGalleryPostPageSchema,
  AdminGalleryPostSchema,
  CanvasGalleryPostListSchema,
  ClassGalleryPostSchema,
  GALLERY_IMAGE_MIME_TYPES,
  GalleryClassListSchema,
  GalleryPostPageSchema,
  GalleryReactionStateSchema,
  GallerySchoolListSchema,
  TeacherClassGalleryListSchema,
  AdminClientReportPageSchema,
  AdminLiveSchema,
  AdminOverviewSchema,
  AdminTeacherActivitySchema,
  AssetSchema,
  AuthSessionEnvelopeSchema,
  BetaApplicationListSchema,
  BetaApplicationSchema,
  CanvasPageSchema,
  CanvasSchema,
  CanvasStateSchema,
  ClassCreateEnvelopeSchema,
  ClassPageSchema,
  ClassSchema,
  HttpErrorEnvelopeSchema,
  OAuthProviderListSchema,
  IMAGE_MIME_TYPES,
  MyClientReportListSchema,
  NotificationListSchema,
  PresentationStudentListSchema,
  RevisionPageSchema,
  RevisionSchema,
  RevisionPreviewEnvelopeSchema,
  RevisionRestoreEnvelopeSchema,
  RosterEnvelopeSchema,
  SchoolSchema,
  SchoolSearchResponseSchema,
  SessionRefreshEnvelopeSchema,
  StudentAccountSchema,
  TeacherPageSchema,
  TeacherSchema,
} from "@canvaschool/contracts";

import { createUuid } from "../editor/canvasStateAdapter";
import { recordClientEvent } from "../feedback/clientLog";

import type {
  AdminAuditEventPage,
  AdminClientReportPage,
  AdminClientReportSource,
  AdminLive,
  AdminOverview,
  AdminTeacherActivity,
  Asset,
  AuditAction,
  AuthSessionEnvelope,
  BetaApplication,
  BetaApplicationList,
  CanvasRecord,
  CanvasState,
  PresentationStudentList,
  ClassRecord,
  ClientReportRequest,
  EmailVerificationPurpose,
  ErrorCode,
  MailUnsubscribeRequest,
  MyClientReport,
  NotificationList,
  OAuthProvider,
  Revision,
  AdminGalleryPost,
  AdminGalleryPostPage,
  ClassGalleryPost,
  GalleryClassList,
  GalleryPostPage,
  GalleryReactionKind,
  GalleryReactionState,
  GalleryReportReason,
  GallerySchoolList,
  TeacherClassGalleryList,
  School,
  SessionRefreshEnvelope,
  StudentAccount,
  Teacher,
  UserStatus,
} from "@canvaschool/contracts";
import type { z } from "zod";

export type ApiErrorCode = ErrorCode | "NETWORK_FAILED" | "INVALID_RESPONSE";

export class ApiError extends Error {
  readonly status: number;
  readonly code: ApiErrorCode;
  /** Seconds until a rate-limited login may be retried, when the server says. */
  readonly retryAfterSeconds: number | null;

  constructor(
    status: number,
    code: ApiErrorCode,
    message: string = code,
    retryAfterSeconds: number | null = null,
  ) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.retryAfterSeconds = retryAfterSeconds;
  }
}

/** What the form sends; the server trims and checks it again. */
export type BetaApplicationRequestInput = {
  email: string;
  code: string;
  name: string;
  /** NEIS standard school code picked from the school search. */
  schoolCode: string;
  message?: string;
  consent: true;
};

export type Page<T> = { items: T[]; nextCursor: string | null };
export type ClassCreateEnvelope = z.infer<typeof ClassCreateEnvelopeSchema>;
export type RevisionPreview = z.infer<typeof RevisionPreviewEnvelopeSchema>;
export type RevisionRestoreResult = z.infer<
  typeof RevisionRestoreEnvelopeSchema
>;
export type ClassListStatus = "ACTIVE" | "ARCHIVED" | "ALL";
export type StudentPatch = { displayName?: string | null; enabled?: boolean };
export type ExpectedCanvasState = {
  expectedEpoch: string;
  expectedVersion: number;
};

export type GalleryListOptions = {
  sort?: "recent" | "popular";
  schoolCode?: string;
  classId?: string;
  cursor?: string;
};

/** The image of a gallery post; the session cookie authorizes it. */
export const galleryImagePath = (postId: string): string =>
  `/api/v1/gallery/posts/${encodeURIComponent(postId)}/image`;

export type CanvaschoolApi = {
  /** Resolves null when there is no valid session. */
  getSession(): Promise<AuthSessionEnvelope | null>;
  login(loginId: string, password: string): Promise<AuthSessionEnvelope>;
  /** Teacher sign-in providers the server offers (Google, Naver). */
  listOAuthProviders(): Promise<OAuthProvider[]>;
  logout(): Promise<void>;
  refreshSession(): Promise<SessionRefreshEnvelope>;
  changePassword(currentPassword: string, newPassword: string): Promise<void>;
  setDisplayName(displayName: string): Promise<StudentAccount>;
  /** Up to 20 NEIS schools whose name contains `q` (2+ characters). */
  searchSchools(q: string, signal?: AbortSignal): Promise<School[]>;
  /** A teacher picks the school of their own account. */
  setMySchool(schoolCode: string): Promise<School>;
  /** An administrator links a teacher account to a school. */
  setTeacherSchool(teacherId: string, schoolCode: string): Promise<Teacher>;
  /** 학교 밖 갤러리: cards for anyone signed in, newest or most loved first. */
  listGalleryPosts(
    options?: GalleryListOptions,
    signal?: AbortSignal,
  ): Promise<GalleryPostPage>;
  /** Schools that have posts, matched by name (2+ characters). */
  searchGallerySchools(
    q: string,
    signal?: AbortSignal,
  ): Promise<GallerySchoolList["schools"]>;
  /** Classes of one school that have posts. */
  listGalleryClasses(schoolCode: string): Promise<GalleryClassList["classes"]>;
  /** Presses (`on`) or releases one reaction bubble. */
  setGalleryReaction(
    postId: string,
    kind: GalleryReactionKind,
    on: boolean,
  ): Promise<GalleryReactionState>;
  reportGalleryPost(postId: string, reason: GalleryReportReason): Promise<void>;
  /** The student takes their post down, or the teacher removes it. */
  withdrawGalleryPost(postId: string): Promise<void>;
  /** The teacher lets a removed frame be posted again. */
  allowGalleryPostAgain(postId: string): Promise<ClassGalleryPost>;
  /** A student posts (or replaces) the image of their sealed frame. */
  publishGalleryPost(
    classId: string,
    canvasId: string,
    frameId: string,
    image: Blob,
  ): Promise<ClassGalleryPost>;
  /** A student's own posts on a canvas; the teacher gets all of them. */
  listCanvasGalleryPosts(
    classId: string,
    canvasId: string,
  ): Promise<ClassGalleryPost[]>;
  /** The teacher's 「갤러리에 올라간 우리 반 작품」. */
  listClassGalleryPosts(classId: string): Promise<TeacherClassGalleryList>;
  markClassGallerySeen(classId: string): Promise<void>;
  listAdminGalleryPosts(options?: {
    filter?: "REPORTED" | "HIDDEN" | "ALL";
    cursor?: string;
  }): Promise<AdminGalleryPostPage>;
  restoreGalleryPost(postId: string): Promise<AdminGalleryPost>;
  deleteGalleryPost(postId: string): Promise<AdminGalleryPost>;
  /** An uncaught error, a typed report, or a survey answer for operators. */
  sendClientReport(report: ClientReportRequest): Promise<void>;
  /** What this user sent through 의견·요청 or the survey, newest first. */
  listMyClientReports(): Promise<MyClientReport[]>;
  /** The header bell's notices; read on page loads, never pushed. */
  listNotifications(): Promise<NotificationList>;
  markNotificationsRead(ids: readonly string[]): Promise<void>;
  /** Admin: answers a report and notifies whoever sent it. */
  replyToClientReport(reportId: string, message: string): Promise<void>;
  listTeachers(options?: {
    q?: string;
    cursor?: string;
  }): Promise<Page<Teacher>>;
  createTeacher(loginId: string): Promise<Teacher>;
  setTeacherStatus(teacherId: string, status: UserStatus): Promise<Teacher>;
  resetTeacherPassword(teacherId: string): Promise<void>;
  /** Enters the teacher's account; the returned session is the teacher's. */
  impersonateTeacher(teacherId: string): Promise<AuthSessionEnvelope>;
  /** Leaves the teacher's account; the cookie returns to the administrator. */
  endImpersonation(): Promise<void>;
  /** Mails a 6-digit code; no sign-in needed. */
  requestEmailVerification(
    email: string,
    purpose: EmailVerificationPurpose,
  ): Promise<void>;
  /** Files an open-beta application with the mailed code; no sign-in needed. */
  submitBetaApplication(body: BetaApplicationRequestInput): Promise<void>;
  /** Stops notice and lesson-tips mail for the address a mailed link signs. */
  unsubscribeMail(token: MailUnsubscribeRequest): Promise<void>;
  listBetaApplications(options?: {
    status?: BetaApplication["status"];
    q?: string;
    cursor?: string;
    limit?: number;
  }): Promise<BetaApplicationList>;
  /** Only PENDING and REJECTED applications can be deleted. */
  deleteBetaApplication(applicationId: string): Promise<void>;
  /** Numbers only; administrators never see class or canvas content. */
  getAdminOverview(): Promise<AdminOverview>;
  /** Funnel counts and sign-in times; no class or canvas names. */
  getAdminTeacherActivity(): Promise<AdminTeacherActivity>;
  /** Server and open-room state: head counts and the teacher's login id only. */
  adminLive(): Promise<AdminLive>;
  listAuditEvents(options?: {
    action?: AuditAction;
    cursor?: string;
  }): Promise<AdminAuditEventPage>;
  listClientReports(options?: {
    source?: AdminClientReportSource;
    cursor?: string;
  }): Promise<AdminClientReportPage>;
  approveBetaApplication(applicationId: string): Promise<BetaApplication>;
  rejectBetaApplication(applicationId: string): Promise<BetaApplication>;
  resendBetaCredentials(applicationId: string): Promise<BetaApplication>;
  listClasses(
    status: ClassListStatus,
    cursor?: string,
  ): Promise<Page<ClassRecord>>;
  createClass(
    name: string,
    accountPrefix: string,
    studentCount: number,
  ): Promise<ClassCreateEnvelope>;
  getClass(classId: string): Promise<ClassRecord>;
  renameClass(classId: string, name: string): Promise<ClassRecord>;
  setGroupCount(classId: string, groupCount: number): Promise<ClassRecord>;
  archiveClass(classId: string): Promise<ClassRecord>;
  reactivateClass(classId: string): Promise<ClassRecord>;
  getRoster(classId: string): Promise<StudentAccount[]>;
  rosterCsvUrl(classId: string): string;
  updateStudent(
    classId: string,
    studentNumber: number,
    patch: StudentPatch,
  ): Promise<StudentAccount>;
  resetStudentPassword(classId: string, studentNumber: number): Promise<void>;
  listCanvases(classId: string, cursor?: string): Promise<Page<CanvasRecord>>;
  listTrashedCanvases(
    classId: string,
    cursor?: string,
  ): Promise<Page<CanvasRecord>>;
  createCanvas(
    classId: string,
    title: string,
    options?: {
      template?: "NONE" | "INDIVIDUAL" | "GROUP" | "PRESENTATION";
      scene?: unknown[];
    },
  ): Promise<CanvasRecord>;
  /** One canvas's record: its kind, and for a presentation student canvas
   * its lesson, owner and completion. */
  getCanvas(classId: string, canvasId: string): Promise<CanvasRecord>;
  /** The teacher's presentation lesson screen: one card per student. */
  listPresentationStudents(
    classId: string,
    lessonId: string,
  ): Promise<PresentationStudentList>;
  /** Finds or creates `studentNumber`'s canvas in a presentation lesson. */
  openPresentationStudentCanvas(
    classId: string,
    lessonId: string,
    studentNumber: number,
  ): Promise<CanvasRecord>;
  /** The owning student checks or unchecks 완료 on their canvas. */
  setCanvasCompletion(
    classId: string,
    canvasId: string,
    completed: boolean,
  ): Promise<CanvasRecord>;
  renameCanvas(
    classId: string,
    canvasId: string,
    title: string,
  ): Promise<CanvasRecord>;
  trashCanvas(classId: string, canvasId: string): Promise<CanvasRecord>;
  restoreCanvas(classId: string, canvasId: string): Promise<CanvasRecord>;
  duplicateCanvas(
    classId: string,
    canvasId: string,
    name: string,
  ): Promise<CanvasRecord>;
  getCanvasState(classId: string, canvasId: string): Promise<CanvasState>;
  /** Stores image bytes under their content-derived file ID. */
  uploadAsset(
    classId: string,
    canvasId: string,
    fileId: string,
    image: Blob,
  ): Promise<Asset>;
  fetchAsset(classId: string, canvasId: string, fileId: string): Promise<Blob>;
  listRevisions(
    classId: string,
    canvasId: string,
    cursor?: string,
  ): Promise<Page<Revision>>;
  /** Marks the current scene as a checkpoint the teacher can restore to. */
  createRevision(classId: string, canvasId: string): Promise<Revision>;
  previewRevision(
    classId: string,
    canvasId: string,
    revisionId: string,
  ): Promise<RevisionPreview>;
  restoreRevision(
    classId: string,
    canvasId: string,
    revisionId: string,
    expected: ExpectedCanvasState,
  ): Promise<RevisionRestoreResult>;
};

type Schema<T> = z.ZodType<T, z.ZodTypeDef, unknown>;

type RequestOptions<T> = {
  method?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
  body?: unknown;
  /** Omitted for routes that answer 204 without a body. */
  schema?: Schema<T>;
  /** Sends one Idempotency-Key for the action and permits one retry. */
  idempotent?: boolean;
  /** Raw bytes sent with their own content type instead of JSON. */
  binaryBody?: Blob;
  /** Resolves the successful response body as a Blob. */
  binaryResponse?: boolean;
  /** Permits one resend of a request that is idempotent without a key. */
  resendable?: boolean;
  /** Cancels the request, e.g. when a newer search replaces it. */
  signal?: AbortSignal;
};

const segment = (value: string | number) => encodeURIComponent(String(value));

/** Authentication routes report their own failures and are never resent. */
const AUTH_ROUTE = /^\/api\/v1\/auth\//;

const query = (values: Record<string, string | undefined>): string => {
  const entries = Object.entries(values).filter(
    (entry): entry is [string, string] => entry[1] !== undefined,
  );
  return entries.length > 0 ? `?${new URLSearchParams(entries)}` : "";
};

const errorFrom = async (response: Response): Promise<ApiError> => {
  try {
    const parsed = HttpErrorEnvelopeSchema.safeParse(await response.json());
    if (parsed.success) {
      const retryAfter = parsed.data.error.details?.retryAfterSeconds;
      return new ApiError(
        response.status,
        parsed.data.error.code,
        parsed.data.error.message,
        typeof retryAfter === "number" ? retryAfter : null,
      );
    }
  } catch {
    // Fall through to a status-derived code for non-JSON failures.
  }
  return new ApiError(
    response.status,
    response.status >= 500 ? "PERSISTENCE_FAILED" : "INVALID_RESPONSE",
  );
};

/**
 * Same-origin client for the Canvaschool HTTP API. The session cookie is
 * HttpOnly; the CSRF token is held only in this closure, never in storage.
 */
export const createApiClient = (
  options: { fetch?: typeof fetch; randomUuid?: () => string } = {},
): CanvaschoolApi => {
  const fetchImpl: typeof fetch =
    options.fetch ?? ((input, init) => window.fetch(input, init));
  // crypto.randomUUID() throws outside a secure context (e.g. plain HTTP
  // dev origins), so fall back to the same manual UUIDv4 the editor uses.
  const randomUuid = options.randomUuid ?? createUuid;
  let csrfToken: string | null = null;
  /** The user this client acts for; a resync never switches users. */
  let sessionUserId: string | null = null;
  let resyncInFlight: Promise<boolean> | null = null;

  /**
   * Adopts the CSRF token of the session the browser cookie now holds, but
   * only when it still belongs to the same user. Parallel callers share one
   * request.
   */
  const resynchronizeSession = (): Promise<boolean> => {
    resyncInFlight ??= (async () => {
      try {
        const response = await fetchImpl("/api/v1/auth/session", {
          method: "GET",
          credentials: "same-origin",
          headers: { Accept: "application/json" },
        });
        if (!response.ok) {
          csrfToken = null;
          return false;
        }
        const parsed = AuthSessionEnvelopeSchema.safeParse(
          await response.json(),
        );
        if (!parsed.success) {
          return false;
        }
        if (
          sessionUserId !== null &&
          parsed.data.principal.userId !== sessionUserId
        ) {
          // Another user signed in on this browser; never act as them.
          csrfToken = null;
          return false;
        }
        csrfToken = parsed.data.csrfToken;
        sessionUserId = parsed.data.principal.userId;
        return true;
      } catch {
        return false;
      } finally {
        resyncInFlight = null;
      }
    })();
    return resyncInFlight;
  };

  const sendUnlogged = async <T>(
    path: string,
    request: RequestOptions<T> = {},
  ): Promise<T> => {
    const method = request.method ?? "GET";
    // One key per user action, reused by every resend of that action.
    const idempotencyKey = request.idempotent ? randomUuid() : null;
    const build = (): RequestInit => {
      const headers: Record<string, string> = {
        Accept: request.binaryResponse ? "*/*" : "application/json",
      };
      if (method !== "GET") {
        headers["Content-Type"] = request.binaryBody
          ? request.binaryBody.type
          : "application/json";
        if (csrfToken) {
          headers["X-CSRF-Token"] = csrfToken;
        }
      }
      if (idempotencyKey) {
        headers["Idempotency-Key"] = idempotencyKey;
      }
      return {
        method,
        credentials: "same-origin",
        headers,
        ...(request.signal ? { signal: request.signal } : {}),
        ...(method === "GET"
          ? {}
          : { body: request.binaryBody ?? JSON.stringify(request.body ?? {}) }),
      };
    };
    // Only safe reads and idempotency-keyed actions may be sent twice.
    const retryable =
      method === "GET" ||
      idempotencyKey !== null ||
      request.resendable === true;
    const transmit = async (): Promise<Response> => {
      try {
        return await fetchImpl(path, build());
      } catch {
        // A cancelled request (a superseded school search) is not resent.
        if (!retryable || request.signal?.aborted) {
          throw new ApiError(0, "NETWORK_FAILED");
        }
        try {
          return await fetchImpl(path, build());
        } catch {
          throw new ApiError(0, "NETWORK_FAILED");
        }
      }
    };
    let response = await transmit();
    if (!response.ok) {
      const error = await errorFrom(response);
      // Another tab may have rotated the session cookie. The server rejects
      // these before any effect, so resynchronize once and resend.
      if (
        !AUTH_ROUTE.test(path) &&
        (error.code === "CSRF_INVALID" || error.code === "UNAUTHENTICATED") &&
        (await resynchronizeSession())
      ) {
        response = await transmit();
        if (!response.ok) {
          throw await errorFrom(response);
        }
      } else {
        throw error;
      }
    }
    if (request.binaryResponse) {
      try {
        return (await response.blob()) as T;
      } catch {
        throw new ApiError(response.status, "INVALID_RESPONSE");
      }
    }
    if (!request.schema) {
      return undefined as T;
    }
    let payload: unknown;
    try {
      payload = await response.json();
    } catch {
      throw new ApiError(response.status, "INVALID_RESPONSE");
    }
    const parsed = request.schema.safeParse(payload);
    if (!parsed.success) {
      throw new ApiError(response.status, "INVALID_RESPONSE");
    }
    return parsed.data;
  };

  // Failed calls join the in-memory event log that reports carry along.
  const send = <T>(path: string, request?: RequestOptions<T>): Promise<T> =>
    sendUnlogged(path, request).catch((error: unknown) => {
      if (request?.signal?.aborted) {
        throw error;
      }
      recordClientEvent(
        "api",
        `${request?.method ?? "GET"} ${path} ${
          error instanceof ApiError ? `${error.status} ${error.code}` : "failed"
        }`,
      );
      throw error;
    });

  const remember = (session: AuthSessionEnvelope) => {
    csrfToken = session.csrfToken;
    sessionUserId = session.principal.userId;
    return session;
  };
  const classPath = (classId: string) => `/api/v1/classes/${segment(classId)}`;
  const canvasPath = (classId: string, canvasId: string) =>
    `${classPath(classId)}/canvases/${segment(canvasId)}`;

  return {
    async getSession() {
      try {
        return remember(
          await send("/api/v1/auth/session", {
            schema: AuthSessionEnvelopeSchema,
          }),
        );
      } catch (error) {
        if (error instanceof ApiError && error.code === "UNAUTHENTICATED") {
          csrfToken = null;
          sessionUserId = null;
          return null;
        }
        throw error;
      }
    },
    login: async (loginId, password) =>
      remember(
        await send("/api/v1/auth/login", {
          method: "POST",
          body: { loginId, password },
          schema: AuthSessionEnvelopeSchema,
        }),
      ),
    listOAuthProviders: async () =>
      (
        await send("/api/v1/auth/oauth/providers", {
          schema: OAuthProviderListSchema,
        })
      ).providers,
    async logout() {
      try {
        await send("/api/v1/auth/logout", { method: "POST", body: {} });
      } finally {
        csrfToken = null;
        sessionUserId = null;
      }
    },
    async refreshSession() {
      const refreshed = await send("/api/v1/auth/session/refresh", {
        method: "POST",
        body: {},
        schema: SessionRefreshEnvelopeSchema,
      });
      csrfToken = refreshed.csrfToken;
      return refreshed;
    },
    sendClientReport: (report) =>
      send("/api/v1/client-reports", { method: "POST", body: report }),
    listMyClientReports: async () =>
      (
        await send("/api/v1/client-reports/mine", {
          schema: MyClientReportListSchema,
        })
      ).items,
    listNotifications: () =>
      send("/api/v1/notifications", { schema: NotificationListSchema }),
    markNotificationsRead: (ids) =>
      send("/api/v1/notifications/read", {
        method: "POST",
        body: { ids: [...ids] },
      }),
    replyToClientReport: (reportId, message) =>
      send(`/api/v1/admin/client-reports/${segment(reportId)}/reply`, {
        method: "POST",
        body: { message },
      }),
    changePassword: (currentPassword, newPassword) =>
      send("/api/v1/auth/change-password", {
        method: "POST",
        body: { currentPassword, newPassword },
      }),
    setDisplayName: (displayName) =>
      send("/api/v1/me/display-name", {
        method: "PUT",
        body: { displayName },
        schema: StudentAccountSchema,
      }),
    searchSchools: async (q, signal) =>
      (
        await send(`/api/v1/schools${query({ q })}`, {
          schema: SchoolSearchResponseSchema,
          signal,
        })
      ).schools,
    setMySchool: (schoolCode) =>
      send("/api/v1/me/school", {
        method: "PUT",
        body: { schoolCode },
        schema: SchoolSchema,
      }),
    listGalleryPosts: (options = {}, signal) =>
      send(
        `/api/v1/gallery/posts${query({
          sort: options.sort,
          schoolCode: options.schoolCode,
          classId: options.classId,
          cursor: options.cursor,
        })}`,
        { schema: GalleryPostPageSchema, signal },
      ),
    searchGallerySchools: async (q, signal) =>
      (
        await send(`/api/v1/gallery/schools${query({ q })}`, {
          schema: GallerySchoolListSchema,
          signal,
        })
      ).schools,
    listGalleryClasses: async (schoolCode) =>
      (
        await send(`/api/v1/gallery/schools/${segment(schoolCode)}/classes`, {
          schema: GalleryClassListSchema,
        })
      ).classes,
    setGalleryReaction: (postId, kind, on) =>
      send(
        `/api/v1/gallery/posts/${segment(postId)}/reactions/${segment(kind)}`,
        on
          ? {
              method: "PUT",
              body: {},
              schema: GalleryReactionStateSchema,
              resendable: true,
            }
          : {
              method: "DELETE",
              schema: GalleryReactionStateSchema,
              resendable: true,
            },
      ),
    reportGalleryPost: (postId, reason) =>
      send(`/api/v1/gallery/posts/${segment(postId)}/reports`, {
        method: "POST",
        body: { reason },
        resendable: true,
      }),
    withdrawGalleryPost: (postId) =>
      send(`/api/v1/gallery/posts/${segment(postId)}`, { method: "DELETE" }),
    allowGalleryPostAgain: (postId) =>
      send(`/api/v1/gallery/posts/${segment(postId)}/allow-again`, {
        method: "POST",
        body: {},
        schema: ClassGalleryPostSchema,
      }),
    async publishGalleryPost(classId, canvasId, frameId, image) {
      if (
        !(GALLERY_IMAGE_MIME_TYPES as readonly string[]).includes(image.type)
      ) {
        throw new ApiError(0, "VALIDATION_FAILED");
      }
      return send(
        `${canvasPath(classId, canvasId)}/frames/${segment(
          frameId,
        )}/gallery-post`,
        { method: "PUT", binaryBody: image, schema: ClassGalleryPostSchema },
      );
    },
    listCanvasGalleryPosts: async (classId, canvasId) =>
      (
        await send(`${canvasPath(classId, canvasId)}/gallery-posts`, {
          schema: CanvasGalleryPostListSchema,
        })
      ).items,
    listClassGalleryPosts: (classId) =>
      send(`${classPath(classId)}/gallery-posts`, {
        schema: TeacherClassGalleryListSchema,
      }),
    markClassGallerySeen: (classId) =>
      send(`${classPath(classId)}/gallery-posts/seen`, {
        method: "POST",
        body: {},
      }),
    listAdminGalleryPosts: (options = {}) =>
      send(
        `/api/v1/admin/gallery-posts${query({
          filter: options.filter,
          cursor: options.cursor,
        })}`,
        { schema: AdminGalleryPostPageSchema },
      ),
    restoreGalleryPost: (postId) =>
      send(`/api/v1/admin/gallery-posts/${segment(postId)}/restore`, {
        method: "POST",
        body: {},
        schema: AdminGalleryPostSchema,
      }),
    deleteGalleryPost: (postId) =>
      send(`/api/v1/admin/gallery-posts/${segment(postId)}/delete`, {
        method: "POST",
        body: {},
        schema: AdminGalleryPostSchema,
      }),
    setTeacherSchool: (teacherId, schoolCode) =>
      send(`/api/v1/admin/teachers/${segment(teacherId)}/school`, {
        method: "PUT",
        body: { schoolCode },
        schema: TeacherSchema,
      }),
    listTeachers: (options = {}) =>
      send(
        `/api/v1/admin/teachers${query({
          q: options.q,
          cursor: options.cursor,
        })}`,
        {
          schema: TeacherPageSchema,
        },
      ),
    createTeacher: (loginId) =>
      send("/api/v1/admin/teachers", {
        method: "POST",
        body: { loginId },
        schema: TeacherSchema,
      }),
    setTeacherStatus: (teacherId, status) =>
      send(`/api/v1/admin/teachers/${segment(teacherId)}/status`, {
        method: "PATCH",
        body: { status },
        schema: TeacherSchema,
      }),
    resetTeacherPassword: (teacherId) =>
      send(`/api/v1/admin/teachers/${segment(teacherId)}/reset-password`, {
        method: "POST",
        body: {},
      }),
    impersonateTeacher: async (teacherId) =>
      remember(
        await send(`/api/v1/admin/teachers/${segment(teacherId)}/impersonate`, {
          method: "POST",
          body: {},
          schema: AuthSessionEnvelopeSchema,
        }),
      ),
    async endImpersonation() {
      try {
        await send("/api/v1/auth/impersonation/end", {
          method: "POST",
          body: {},
        });
      } finally {
        csrfToken = null;
        sessionUserId = null;
      }
    },
    requestEmailVerification: (email, purpose) =>
      send("/api/v1/email-verifications", {
        method: "POST",
        body: { email, purpose },
      }),
    submitBetaApplication: (body) =>
      send("/api/v1/beta-applications", { method: "POST", body }),
    unsubscribeMail: async (token) => {
      // The token travels in the body so it never appears in logged paths.
      await send("/api/v1/mail/unsubscribe", { method: "POST", body: token });
    },
    listBetaApplications: (options = {}) =>
      send(
        `/api/v1/admin/beta-applications${query({
          status: options.status,
          q: options.q,
          cursor: options.cursor,
          limit:
            options.limit === undefined ? undefined : String(options.limit),
        })}`,
        { schema: BetaApplicationListSchema },
      ),
    deleteBetaApplication: (applicationId) =>
      send(`/api/v1/admin/beta-applications/${segment(applicationId)}`, {
        method: "DELETE",
      }),
    getAdminOverview: () =>
      send("/api/v1/admin/overview", { schema: AdminOverviewSchema }),
    getAdminTeacherActivity: () =>
      send("/api/v1/admin/teacher-activity", {
        schema: AdminTeacherActivitySchema,
      }),
    adminLive: () => send("/api/v1/admin/live", { schema: AdminLiveSchema }),
    listAuditEvents: (options = {}) =>
      send(
        `/api/v1/admin/audit-events${query({
          action: options.action,
          cursor: options.cursor,
        })}`,
        { schema: AdminAuditEventPageSchema },
      ),
    listClientReports: (options = {}) =>
      send(
        `/api/v1/admin/client-reports${query({
          source: options.source,
          cursor: options.cursor,
        })}`,
        { schema: AdminClientReportPageSchema },
      ),
    approveBetaApplication: (applicationId) =>
      send(
        `/api/v1/admin/beta-applications/${segment(applicationId)}/approve`,
        {
          method: "POST",
          body: {},
          schema: BetaApplicationSchema,
        },
      ),
    rejectBetaApplication: (applicationId) =>
      send(`/api/v1/admin/beta-applications/${segment(applicationId)}/reject`, {
        method: "POST",
        body: {},
        schema: BetaApplicationSchema,
      }),
    resendBetaCredentials: (applicationId) =>
      send(`/api/v1/admin/beta-applications/${segment(applicationId)}/resend`, {
        method: "POST",
        body: {},
        schema: BetaApplicationSchema,
      }),
    listClasses: (status, cursor) =>
      send(`/api/v1/classes${query({ status, cursor })}`, {
        schema: ClassPageSchema,
      }),
    createClass: (name, accountPrefix, studentCount) =>
      send("/api/v1/classes", {
        method: "POST",
        body: { name, accountPrefix, studentCount },
        schema: ClassCreateEnvelopeSchema,
        idempotent: true,
      }),
    getClass: (classId) => send(classPath(classId), { schema: ClassSchema }),
    renameClass: (classId, name) =>
      send(classPath(classId), {
        method: "PATCH",
        body: { name },
        schema: ClassSchema,
      }),
    setGroupCount: (classId, groupCount) =>
      send(`${classPath(classId)}/group-count`, {
        method: "PATCH",
        body: { groupCount },
        schema: ClassSchema,
      }),
    archiveClass: (classId) =>
      send(`${classPath(classId)}/archive`, {
        method: "POST",
        body: {},
        schema: ClassSchema,
        idempotent: true,
      }),
    reactivateClass: (classId) =>
      send(`${classPath(classId)}/reactivate`, {
        method: "POST",
        body: {},
        schema: ClassSchema,
        idempotent: true,
      }),
    getRoster: async (classId) =>
      (
        await send(`${classPath(classId)}/roster`, {
          schema: RosterEnvelopeSchema,
        })
      ).items,
    rosterCsvUrl: (classId) => `${classPath(classId)}/roster.csv`,
    updateStudent: (classId, studentNumber, patch) =>
      send(`${classPath(classId)}/students/${segment(studentNumber)}`, {
        method: "PATCH",
        body: patch,
        schema: StudentAccountSchema,
      }),
    resetStudentPassword: (classId, studentNumber) =>
      send(
        `${classPath(classId)}/students/${segment(
          studentNumber,
        )}/reset-password`,
        { method: "POST", body: {} },
      ),
    listCanvases: (classId, cursor) =>
      send(`${classPath(classId)}/canvases${query({ cursor })}`, {
        schema: CanvasPageSchema,
      }),
    listTrashedCanvases: (classId, cursor) =>
      send(`${classPath(classId)}/canvases/trash${query({ cursor })}`, {
        schema: CanvasPageSchema,
      }),
    createCanvas: (classId, title, options) =>
      send(`${classPath(classId)}/canvases`, {
        method: "POST",
        body: {
          title,
          ...(options?.template ? { template: options.template } : {}),
          ...(options?.scene ? { scene: options.scene } : {}),
        },
        schema: CanvasSchema,
        idempotent: true,
      }),
    getCanvas: (classId, canvasId) =>
      send(canvasPath(classId, canvasId), { schema: CanvasSchema }),
    listPresentationStudents: (classId, lessonId) =>
      send(
        `${classPath(classId)}/presentations/${segment(lessonId)}/students`,
        { schema: PresentationStudentListSchema },
      ),
    openPresentationStudentCanvas: (classId, lessonId, studentNumber) =>
      send(
        `${classPath(classId)}/presentations/${segment(
          lessonId,
        )}/students/${studentNumber}/canvas`,
        { method: "POST", body: {}, schema: CanvasSchema },
      ),
    setCanvasCompletion: (classId, canvasId, completed) =>
      send(`${canvasPath(classId, canvasId)}/completion`, {
        method: "PUT",
        body: { completed },
        schema: CanvasSchema,
      }),
    renameCanvas: (classId, canvasId, title) =>
      send(canvasPath(classId, canvasId), {
        method: "PATCH",
        body: { title },
        schema: CanvasSchema,
      }),
    trashCanvas: (classId, canvasId) =>
      send(`${canvasPath(classId, canvasId)}/trash`, {
        method: "POST",
        body: {},
        schema: CanvasSchema,
        idempotent: true,
      }),
    restoreCanvas: (classId, canvasId) =>
      send(`${canvasPath(classId, canvasId)}/restore`, {
        method: "POST",
        body: {},
        schema: CanvasSchema,
        idempotent: true,
      }),
    duplicateCanvas: (classId, canvasId, name) =>
      send(`${canvasPath(classId, canvasId)}/duplicate`, {
        method: "POST",
        body: { name },
        schema: CanvasSchema,
        idempotent: true,
      }),
    getCanvasState: (classId, canvasId) =>
      send(`${canvasPath(classId, canvasId)}/state`, {
        schema: CanvasStateSchema,
      }),
    async uploadAsset(classId, canvasId, fileId, image) {
      if (!(IMAGE_MIME_TYPES as readonly string[]).includes(image.type)) {
        throw new ApiError(0, "VALIDATION_FAILED");
      }
      return send(
        `${canvasPath(classId, canvasId)}/assets/${segment(fileId)}`,
        {
          method: "PUT",
          binaryBody: image,
          schema: AssetSchema,
          // The file ID is derived from the bytes, so a resend is harmless.
          resendable: true,
        },
      );
    },
    fetchAsset: (classId, canvasId, fileId) =>
      send<Blob>(`${canvasPath(classId, canvasId)}/assets/${segment(fileId)}`, {
        binaryResponse: true,
      }),
    listRevisions: (classId, canvasId, cursor) =>
      send(`${canvasPath(classId, canvasId)}/revisions${query({ cursor })}`, {
        schema: RevisionPageSchema,
      }),
    createRevision: (classId, canvasId) =>
      send(`${canvasPath(classId, canvasId)}/revisions`, {
        method: "POST",
        body: {},
        schema: RevisionSchema,
        idempotent: true,
      }),
    previewRevision: (classId, canvasId, revisionId) =>
      send(
        `${canvasPath(classId, canvasId)}/revisions/${segment(revisionId)}`,
        {
          schema: RevisionPreviewEnvelopeSchema,
        },
      ),
    restoreRevision: (classId, canvasId, revisionId, expected) =>
      send(
        `${canvasPath(classId, canvasId)}/revisions/${segment(
          revisionId,
        )}/restore`,
        {
          method: "POST",
          body: expected,
          schema: RevisionRestoreEnvelopeSchema,
          idempotent: true,
        },
      ),
  };
};
